import { useCallback, useState } from 'react';
import type { TourId } from '@proofroom/schema';
import { tours } from '../api/endpoints';
import { useSession } from '../auth/session';

/** A guest has no user row to stamp; their browser remembers instead (private windows forget, which is fine) */
const GUEST_KEY = 'pr_tour_guest';

function guestSeen(): boolean {
  try { return !!window.localStorage.getItem(GUEST_KEY); } catch { return false; }
}

function rememberGuest(): void {
  try { window.localStorage.setItem(GUEST_KEY, new Date().toISOString()); } catch { /* storage refused: the tour shows again next time */ }
}

export interface TourControl {
  open: boolean;
  /** The header's Show me around link */
  start(): void;
  /** Finished or skipped, the tour is stamped either way: the map says when it was last dismissed */
  close(finished: boolean): void;
}

/**
 * Open on mount for anyone who has not seen tour `id`: a user by `toursSeen` on their session,
 * a guest by the browser. Closing stamps it, and folds the server's map into the session so a
 * remount of the same screen does not open it again.
 */
export function useTour(id: TourId): TourControl {
  const { user, patchUser } = useSession();
  const [open, setOpen] = useState(() => (user ? !user.toursSeen?.[id] : !guestSeen()));
  const start = useCallback(() => setOpen(true), []);
  const close = useCallback((_finished: boolean) => {
    setOpen(false);
    if (!user) { rememberGuest(); return; }
    tours.seen(id)
      .then((map) => patchUser({ toursSeen: map }))
      .catch((e) => console.warn('[tour] could not record the tour as seen', e));
  }, [id, user, patchUser]);
  return { open, start, close };
}
